import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2, Award, Shield, BadgeCheck, Star, Briefcase, FileCheck } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BADGE_OPTIONS = [
  { id: 'id_verified', label: 'ID Verified', icon: Shield },
  { id: 'license_verified', label: 'License Verified', icon: FileCheck },
  { id: 'insurance_verified', label: 'Insurance Verified', icon: BadgeCheck },
  { id: 'training_completed', label: 'Training Completed', icon: Briefcase },
  { id: 'top_rated', label: 'Top Rated', icon: Star },
  { id: 'background_checked', label: 'Background Checked', icon: Award },
];

const DriverBadgesModal = ({ isOpen, onClose, driver }) => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState('');
  const [notes, setNotes] = useState('');

  const driverUserId = driver?.userId;

  useEffect(() => {
    if (!isOpen || !driverUserId) return;
    const fetchBadges = async () => {
      setLoading(true);
      try {
        const api = (await import('../../utils/api')).default;
        const { data } = await api.get(`/admin/drivers/${driverUserId}/badges`);
        setBadges(data.badges || []);
      } catch (err) {
        toast({ title: 'Error', description: 'Failed to load badges', variant: 'destructive' });
      } finally { setLoading(false); }
    };
    fetchBadges();
  }, [isOpen, driverUserId]);

  const handleAdd = async (badgeType) => {
    setAdding(badgeType);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.post(`/admin/drivers/${driverUserId}/badges`, {
        badge: badgeType,
        notes: notes.trim() || null,
      });
      setBadges(data.badges || []);
      setNotes('');
      toast({ title: 'Badge added' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setAdding(''); }
  };

  const handleRemove = async (badgeType) => {
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.delete(`/admin/drivers/${driverUserId}/badges/${badgeType}`);
      setBadges(data.badges || []);
      toast({ title: 'Badge removed' });
    } catch {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const hasBadge = (id) => badges.some(b => b.type === id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="driver-badges-modal">
        <DialogHeader>
          <DialogTitle>Verification Badges — {driver?.name || 'Driver'}</DialogTitle>
        </DialogHeader>

        {loading && <p className="text-sm text-gray-500">Loading...</p>}

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {BADGE_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const active = hasBadge(opt.id);
            const badge = badges.find(b => b.type === opt.id);
            return (
              <div key={opt.id} className={`flex items-center justify-between p-2 border rounded-lg ${active ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${active ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-medium">{opt.label}</p>
                    {active && badge?.notes && <p className="text-[10px] text-gray-500">{badge.notes}</p>}
                  </div>
                </div>
                {active ? (
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleRemove(opt.id)} data-testid={`remove-badge-${opt.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <Button size="sm" className="bg-pink-600 hover:bg-pink-700" disabled={adding === opt.id} onClick={() => handleAdd(opt.id)} data-testid={`add-badge-${opt.id}`}>
                    {adding === opt.id ? '...' : 'Award'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div>
          <Label>Notes for next badge (optional)</Label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g., NRC checked Mar 2026" />
        </div>

        <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
      </DialogContent>
    </Dialog>
  );
};

export default DriverBadgesModal;
